import type {SponsorCategory} from "sponsor/sponsorBooking";
import type {PageThemeTemplateKey} from "sponsor/sponsorOptions";
import {HiddenRadio} from "sponsor/BackdropTemplatePicker.styles";
import {VisuallyHidden} from "components/Accessibility.styles";
import type {PersonalizationTemplateGroup} from "../personalization/personalizationTemplateGroups";
import {
	GLYPH_MARK_COUNT, GlyphMark, GroupLabel, Tile, TileGlyph, TileGrid, TileGroup, TileName,
} from "./TemplateTilePicker.styles";

/**
 * The transfer builder's COMPACT animation picker (kitecorp/transferify-ui#1953, owner-approved
 * prototype 2026-09-03).
 *
 * ## Why this is not a `compact` prop on `BackdropTemplatePicker`
 *
 * That was the first thing tried, and the two pickers share only the radio mechanics. A card carries a
 * live 16:10 stage running the REAL takeover renderer, a "Live preview" badge, a name, a description
 * paragraph, an arity chip and a sibling "view larger" launcher; a tile carries a 44px static glyph and
 * a name. Every child differs, the grids differ (`auto-fit` reflow against a fixed three columns), and
 * the card picker's own props — `assets`, `video` — mean nothing to a sender composing one transfer. A
 * `compact` flag would have been a component whose two branches share a signature and nothing else,
 * which is two components wearing one name. What IS shared is shared: {@link HiddenRadio}, so the
 * visually-hidden recipe that keeps arrow-key navigation and the screen-reader announcement is one
 * decision rather than two.
 *
 * The ACCOUNT page-theme picker and the sponsor booking form keep the cards, deliberately: an advertiser
 * choosing what to buy is judging the motion itself, and there the live preview is the product.
 *
 * ## What a tile says
 *
 * The glyph is a static composition — a fanned deck, a row of bars, a receding cascade — not a second
 * animation implementation. The motion lives in the live miniature beside the builder, which shows the
 * CHOSEN treatment moving; nine simultaneous loops in a picker would compete with it for the sender's
 * attention and for the frame budget. The description is no longer drawn under the name: it is the
 * tile's `title` for a pointer and its `aria-describedby` for a screen reader, so nothing was dropped
 * from the accessibility tree when the paragraph came off the screen.
 */

/** Everything this picker renders as words. Names come from the caller's catalogue, never from here. */
export interface TemplateTileCopy {
	/** The group headings, by category. */
	categories: Record<SponsorCategory, string>;
	/** The treatments' own names and descriptions. */
	templates: Record<PageThemeTemplateKey, {name: string; description: string}>;
	/** The "no animation" choice — a value of the same exclusive group, so it is named the same way. */
	none: {name: string; description: string};
}

interface Props {
	/** The labelled groups, in order — {@link personalizationTemplateGroups}' answer, never a filter here. */
	groups: readonly PersonalizationTemplateGroup[];
	value: PageThemeTemplateKey | null;
	onChange: (template: PageThemeTemplateKey | null) => void;
	disabled: boolean;
	/**
	 * The radio group's form name, shared by every group this picker renders.
	 *
	 * Same-name radios are ONE exclusive group to the browser however they are nested, so a sender
	 * arrows from the last backdrop into the first wallpaper and can only ever hold one treatment. Two
	 * names would let a sender select one of each.
	 */
	name: string;
	/** Accessible name for the whole picker — the section's own label. */
	label: string;
	copy: TemplateTileCopy;
}

export function TemplateTilePicker({groups, value, onChange, disabled, name, label, copy}: Props) {
	return (
		<div role="radiogroup" aria-label={label}>
			{groups.map(({category, templates}) => (
				<TileGroup key={category}>
					<GroupLabel>{copy.categories[category]}</GroupLabel>
					<TileGrid>
						{templates.map(template => {
							const words = template === null ? copy.none : copy.templates[template];
							const descriptionId = `personalise-tile-${template ?? "none"}-description`;
							return (
								// The tile is a `<label>`, so a click anywhere in it selects.
								<Tile
									key={template ?? "none"}
									$selected={template === value}
									$disabled={disabled}
									title={words.description}
								>
									<HiddenRadio
										type="radio"
										name={name}
										// Named explicitly: the wrapping label is the whole tile, so without
										// this the accessible name would be whatever text it contains. The
										// visible name is exactly this string, so the accessible name still
										// contains the visible label (WCAG 2.5.3).
										aria-label={words.name}
										aria-describedby={descriptionId}
										value={template ?? ""}
										checked={template === value}
										disabled={disabled}
										onChange={() => onChange(template)}
									/>
									<TileGlyph $template={template} aria-hidden="true">
										{/* One element per mark; the composition itself is the stylesheet's. */}
										{Array.from(
											{length: template === null ? 0 : GLYPH_MARK_COUNT[template]},
											(_unused, index) => <GlyphMark key={index}/>,
										)}
									</TileGlyph>
									<TileName>{words.name}</TileName>
									{/* Off the screen, still in the accessibility tree — see the note above. */}
									<VisuallyHidden id={descriptionId}>{words.description}</VisuallyHidden>
								</Tile>
							);
						})}
					</TileGrid>
				</TileGroup>
			))}
		</div>
	);
}
